import React from 'react';

import {assertNever} from 'util/Types';

import {SaveToEntryOption} from './LLMMarkdownSection';

// Allowlist of permitted link domains
export const ALLOWED_LINK_DOMAINS = ['help.benchling.com'];

// Maximum URL length to prevent abuse
const MAX_URL_LENGTH = 1024;

/**
 * Validates that a URL is allowed based on security restrictions:
 * - Only allows https protocol
 * - Checks if the hostname matches the allowlist
 * - Rejects URLs longer than 1024 characters
 * - Rejects URLs with ports other than none (default) or 443
 */
export const isAllowedUrl = (url: string): boolean => {
  // Check URL length first for efficiency
  if (url.length > MAX_URL_LENGTH) {
    return false;
  }

  try {
    const parsedUrl = new URL(url);

    // Only allow https protocol
    if (parsedUrl.protocol !== 'https:') {
      return false;
    }

    // Check port restriction: only allow default (empty string) or 443
    // URL.port returns empty string for default port
    if (parsedUrl.port !== '' && parsedUrl.port !== '443') {
      return false;
    }

    const hostname = parsedUrl.hostname;
    return ALLOWED_LINK_DOMAINS.some((allowedDomain) => hostname === allowedDomain);
  } catch (error) {
    // Invalid URL format
    if (error instanceof TypeError) {
      return false;
    }
    throw error;
  }
};

/**
 * Parses a web citation/source URL into a sanitized href and its hostname.
 *
 * Query params and fragments are stripped from the href as a security
 * mitigation, since we render untrusted URLs from web search and citation
 * sources as clickable links. `domain` is the raw hostname; callers that want
 * a prettier label (e.g. dropping a leading `www.`) should format it
 * themselves. Returns null for unparseable URLs so callers can render a plain
 * label instead of a link.
 */
export function getWebCitationUrlParts(url: string): {href: string; domain: string} | null {
  try {
    const parsedUrl = new URL(url);
    return {
      href: `${parsedUrl.origin}${parsedUrl.pathname}`,
      domain: parsedUrl.hostname,
    };
  } catch (error) {
    // URL constructor throws TypeError for malformed URLs.
    if (error instanceof TypeError) {
      return null;
    }
    throw error;
  }
}

export enum BenchlingTagProperty {
  ID_VALUE = 'idValue',
  LINK_URL = 'linkUrl',
  // The following properties are only used by bnch_file
  FILE_ID = 'fileId',
  FILENAME = 'filename',
  MEDIA_TYPE = 'mediaType',
}

/**
 * Validates and parses a bnch_file tag's JSON content.
 * Returns the parsed data if valid, null if invalid.
 */
export function parseBnchFileJSON(
  content: string
): {fileId: string; filename: string; mediaType: string} | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch (error) {
    // JSON.parse throws SyntaxError for invalid JSON
    if (error instanceof SyntaxError) {
      return null;
    }
    throw error;
  }

  // Check that it's an object (not array or primitive)
  if (typeof parsed !== 'object' || parsed == null || Array.isArray(parsed)) {
    return null;
  }

  const parsedObj = parsed as Record<string, unknown>;

  // Check that only expected properties are present (no additional properties)
  const expectedKeys = ['fileId', 'filename', 'mediaType'];
  const actualKeys = Object.keys(parsedObj);
  if (actualKeys.length !== expectedKeys.length || !actualKeys.every((key) => expectedKeys.includes(key))) {
    return null;
  }

  // Validate each required property
  const isValid =
    typeof parsedObj.fileId === 'string' &&
    // Keep in sync with benchling/models/mixins/api_identified/api_identified.py:MAX_API_ID_LENGTH
    /^efl_[a-zA-Z0-9]{8,255}$/.test(parsedObj.fileId) &&
    typeof parsedObj.filename === 'string' &&
    parsedObj.filename.length > 0 &&
    typeof parsedObj.mediaType === 'string' &&
    parsedObj.mediaType.length > 0;

  if (!isValid) {
    return null;
  }

  return parsedObj as {fileId: string; filename: string; mediaType: string};
}

interface BaseBenchlingTagConfig {
  markdownTag: string | null;
  htmlTagName: string;
  properties: Array<BenchlingTagProperty>;
  validator: (content: string) => boolean;
  component: React.ComponentType<Record<string, unknown>>;
}

interface BenchlingTagConfigWithPreserveTags extends BaseBenchlingTagConfig {
  preserveContentWhenSaving: SaveToEntryOption.PRESERVE_TAGS;
  savingComponent: React.ComponentType<Record<string, unknown>>;
}

interface BenchlingTagConfigWithoutPreserveTags extends BaseBenchlingTagConfig {
  preserveContentWhenSaving: SaveToEntryOption.PRESERVE_TEXT | SaveToEntryOption.REMOVE;
  savingComponent?: never;
}

export type BenchlingTagConfig = BenchlingTagConfigWithPreserveTags | BenchlingTagConfigWithoutPreserveTags;

export interface BenchlingTagConfigs {
  tagConfigs: Array<BenchlingTagConfig>;
}

/**
 * Removes Benchling tags that shouldn't be kept when saving from markdown content
 */
export const cleanMarkdownForSaving = (markdown: string, tagConfigs: BenchlingTagConfigs): string => {
  let cleanedMarkdown = markdown;
  tagConfigs.tagConfigs.forEach((config: BenchlingTagConfig) => {
    const htmlTagRegex = new RegExp(`<${config.markdownTag}>([\\s\\S]*?)</${config.markdownTag}>`, 'g');

    const preserveContentWhenSaving = config.preserveContentWhenSaving;
    switch (preserveContentWhenSaving) {
      case SaveToEntryOption.REMOVE:
        cleanedMarkdown = cleanedMarkdown.replace(htmlTagRegex, '');
        break;
      case SaveToEntryOption.PRESERVE_TEXT:
        cleanedMarkdown = cleanedMarkdown.replace(htmlTagRegex, '$1');
        break;
      case SaveToEntryOption.PRESERVE_TAGS:
        break;
      default:
        assertNever(preserveContentWhenSaving);
        break;
    }
  });

  return cleanedMarkdown;
};

/**
 * Recursively extracts text content from React elements
 */
/** True when a markdown paragraph contains only emphasized (italic) text — used as table subtitles in reports. */
export function isItalicOnlyMarkdownParagraph(children: React.ReactNode): boolean {
  const nodes = React.Children.toArray(children);
  if (nodes.length !== 1) {
    return false;
  }
  const child = nodes[0];
  return React.isValidElement(child) && (child.type === 'em' || child.type === 'i');
}

export const extractTextFromReactNode = (node: React.ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map((n) => extractTextFromReactNode(n)).join('');
  }

  if (React.isValidElement(node)) {
    return extractTextFromReactNode(node.props.children);
  }

  return '';
};

/**
 * Parses table data from React children.
 *
 * Each cell is an array of React nodes since cells can contain multiple elements.
 */
export const parseTableData = (
  children: React.ReactNode
): {headers: Array<string>; rows: Array<Array<Array<React.ReactNode>>>} | null => {
  const childrenArray = React.Children.toArray(children);
  const thead = childrenArray.find((child: unknown) => React.isValidElement(child) && child.type === 'thead');
  const tbody = childrenArray.find((child: unknown) => React.isValidElement(child) && child.type === 'tbody');

  if (!thead) {
    return null;
  }

  // Extract headers from thead
  const theadElement = thead as React.ReactElement;
  const headerRow = React.Children.toArray(theadElement.props.children).find(
    (child: unknown) => React.isValidElement(child) && child.type === 'tr'
  ) as React.ReactElement | undefined;

  if (!headerRow) {
    return null;
  }

  const headers = React.Children.toArray(headerRow.props.children).map((th: unknown) => {
    if (React.isValidElement(th) && th.type === 'th') {
      const thElement = th as React.ReactElement;
      return extractTextFromReactNode(thElement.props.children);
    }
    return '';
  });

  // Extract rows from tbody (if it exists)
  let rows: Array<Array<Array<React.ReactNode>>> = [];
  if (tbody) {
    const tbodyElement = tbody as React.ReactElement;
    rows = React.Children.toArray(tbodyElement.props.children)
      .filter((child: unknown) => React.isValidElement(child) && child.type === 'tr')
      .map((tr: unknown) => {
        const trElement = tr as React.ReactElement;
        return React.Children.toArray(trElement.props.children)
          .filter((child: unknown) => React.isValidElement(child) && child.type === 'td')
          .map((td: unknown) => {
            const tdElement = td as React.ReactElement;
            return React.Children.toArray(tdElement.props.children);
          });
      });
  }

  return {headers, rows};
};
